import { useMemo, useState } from 'react';

import type { Point2D, Polygon2D, Project, RoofModule, RoofPlanGeometry } from '../../gen/ejot/roofing/v1/types';
import { summarizeSitePairs } from './siteGeometry';
import { CoordinateIndicator } from '../../shared/CoordinateIndicator';
import type { LayerVisibility } from '../roof-plan-editor/render';
import './SiteLayoutEditor.css';

interface SiteLayoutEditorProps {
  project: Project;
  layers?: LayerVisibility;
  activeModuleId?: string;
  onActiveModuleChange?: (moduleId: string) => void;
  onOpenModule?: (moduleId: string) => void;
  onProjectChange: (project: Project) => void;
}

interface ViewBox {
  minX: number;
  minY: number;
  width: number;
  height: number;
}

const DEFAULT_OFFSET_MM = 5000;

export function SiteLayoutEditor({
  project,
  layers = {},
  activeModuleId,
  onActiveModuleChange,
  onOpenModule,
  onProjectChange,
}: SiteLayoutEditorProps) {
  const modules = useMemo(() => project.modules ?? [], [project.modules]);
  const [localSelection, setLocalSelection] = useState<string>(() => activeModuleId ?? modules[0]?.id ?? '');
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());
  const [locked, setLocked] = useState<Set<string>>(() => new Set());
  const [translateX, setTranslateX] = useState('0');
  const [translateY, setTranslateY] = useState('0');
  const [rotateDegrees, setRotateDegrees] = useState('0');

  const selectedId = activeModuleId || localSelection || modules[0]?.id || '';
  const selectedModule = modules.find((module) => module.id === selectedId) ?? modules[0];
  const selectModule = (id: string) => {
    setLocalSelection(id);
    onActiveModuleChange?.(id);
  };

  const pairSummaries = useMemo(() => summarizeSitePairs(modules), [modules]);
  const viewBox = useMemo(() => siteViewBox(modules.filter((module) => !hidden.has(module.id ?? ''))), [modules, hidden]);

  const replaceModule = (moduleId: string, producer: (module: RoofModule) => RoofModule) => {
    onProjectChange({
      ...project,
      modules: modules.map((module) => module.id === moduleId ? producer(module) : module),
    });
  };

  const addRoof = () => {
    const source = selectedModule ?? modules[0];
    const next = source ? duplicateModule(source, modules.length + 1) : emptyModule(modules.length + 1);
    onProjectChange({ ...project, modules: [...modules, next] });
    selectModule(next.id ?? '');
  };

  const duplicateRoof = () => {
    if (!selectedModule) return;
    const next = duplicateModule(selectedModule, modules.length + 1);
    onProjectChange({ ...project, modules: [...modules, next] });
    selectModule(next.id ?? '');
  };

  const deleteRoof = () => {
    if (!selectedModule || modules.length <= 1) return;
    const selectedIndex = modules.findIndex((module) => module.id === selectedModule.id);
    const remaining = modules.filter((module) => module.id !== selectedModule.id);
    const fallback = remaining[Math.min(Math.max(selectedIndex, 0), remaining.length - 1)] ?? remaining[0];
    onProjectChange({ ...project, modules: remaining });
    if (fallback?.id) selectModule(fallback.id);
  };

  const applyTranslation = () => {
    if (!selectedModule || locked.has(selectedModule.id ?? '')) return;
    const dx = Number(translateX);
    const dy = Number(translateY);
    if (!Number.isFinite(dx) || !Number.isFinite(dy)) return;
    replaceModule(selectedModule.id ?? '', (module) => transformModulePlan(module, (point) => ({
      ...point,
      x: (point.x ?? 0) + dx,
      y: (point.y ?? 0) + dy,
    })));
    setTranslateX('0');
    setTranslateY('0');
  };

  const applyRotation = () => {
    if (!selectedModule || locked.has(selectedModule.id ?? '')) return;
    const degrees = Number(rotateDegrees);
    if (!Number.isFinite(degrees)) return;
    replaceModule(selectedModule.id ?? '', (module) => rotateModule(module, degrees));
    setRotateDegrees('0');
  };

  const selectedBounds = selectedModule ? moduleBounds(selectedModule) : undefined;

  return (
    <div className="site-layout-editor">
      <aside className="site-layout-sidebar" aria-label="Site roofs">
        <div className="site-layout-toolbar">
          <button type="button" className="toolbar-button" onClick={addRoof}>Add roof</button>
          <button type="button" className="toolbar-button" disabled={!selectedModule} onClick={duplicateRoof}>Duplicate</button>
          <button type="button" className="toolbar-button" disabled={!selectedModule || modules.length <= 1} onClick={deleteRoof}>Delete</button>
        </div>

        <p className="site-layout-scope-note">
          Site Layout moves complete roofs relative to each other. Vertex and shape editing belongs to each roof's Roof Geometry workspace.
        </p>

        <div className="site-building-list">
          {modules.map((module, index) => {
            const id = module.id ?? `module-${index + 1}`;
            const isSelected = id === selectedId;
            return (
              <button
                key={id}
                type="button"
                className={isSelected ? 'site-building-card is-selected' : 'site-building-card'}
                onClick={() => selectModule(id)}
                onDoubleClick={() => onOpenModule?.(id)}
                title="Double-click to open this roof"
              >
                <span className="site-building-title">{module.display_name || `Roof ${index + 1}`}</span>
                <span className="site-building-meta">h = {formatMetres(module.roof_height_above_ground?.millimetres ?? 0)}</span>
              </button>
            );
          })}
        </div>

        {selectedModule && (
          <RoofProperties
            module={selectedModule}
            hidden={hidden.has(selectedModule.id ?? '')}
            locked={locked.has(selectedModule.id ?? '')}
            onRename={(name) => replaceModule(selectedModule.id ?? '', (module) => ({ ...module, display_name: name }))}
            onHeight={(height) => replaceModule(selectedModule.id ?? '', (module) => ({ ...module, roof_height_above_ground: { millimetres: height, origin: 'VALUE_ORIGIN_INPUT' } }))}
            onHidden={(value) => setHidden((current) => toggleSet(current, selectedModule.id ?? '', value))}
            onLocked={(value) => setLocked((current) => toggleSet(current, selectedModule.id ?? '', value))}
          />
        )}

        {selectedModule && (
          <div className="site-placement-summary" aria-label="Selected roof site position">
            <span>Site X <strong>{formatMetres(selectedBounds?.minX ?? 0)}</strong></span>
            <span>Site Y <strong>{formatMetres(selectedBounds?.minY ?? 0)}</strong></span>
          </div>
        )}

        <div className="site-transform-grid" role="group" aria-label="Selected roof placement transform">
          <label>Move X (mm)<input value={translateX} onChange={(event) => setTranslateX(event.target.value)} inputMode="decimal" /></label>
          <label>Move Y (mm)<input value={translateY} onChange={(event) => setTranslateY(event.target.value)} inputMode="decimal" /></label>
          <button type="button" className="toolbar-button" disabled={!selectedModule || locked.has(selectedModule?.id ?? '')} onClick={applyTranslation}>Move roof</button>
          <label>Rotate by (°)<input value={rotateDegrees} onChange={(event) => setRotateDegrees(event.target.value)} inputMode="decimal" /></label>
          <button type="button" className="toolbar-button" disabled={!selectedModule || locked.has(selectedModule?.id ?? '')} onClick={applyRotation}>Rotate roof</button>
          <button type="button" className="toolbar-button" disabled={!selectedModule} onClick={() => selectedModule?.id && onOpenModule?.(selectedModule.id)}>Open roof</button>
        </div>
      </aside>

      <main className="site-layout-main">
        <div className="site-layout-canvas-wrap">
          <svg
            className="site-layout-svg"
            viewBox={`${viewBox.minX} ${-viewBox.minY - viewBox.height} ${viewBox.width} ${viewBox.height}`}
            preserveAspectRatio="xMidYMid meet"
            role="img"
            aria-label="Multi-roof site layout"
          >
            <g transform="scale(1 -1)">
              {modules.filter((module) => !hidden.has(module.id ?? '')).map((module, index) => (
                <ModulePolygon
                  key={module.id ?? index}
                  module={module}
                  selected={(module.id ?? '') === selectedId}
                  onSelect={() => selectModule(module.id ?? '')}
                  onOpen={() => onOpenModule?.(module.id ?? '')}
                />
              ))}
            </g>
          </svg>
          <CoordinateIndicator mode="2d" visible={layers.axes !== false} />
        </div>

        <SiteInteractionTable modules={modules} summaries={pairSummaries} />
      </main>
    </div>
  );
}

function RoofProperties({
  module, hidden, locked, onRename, onHeight, onHidden, onLocked,
}: {
  module: RoofModule;
  hidden: boolean;
  locked: boolean;
  onRename: (name: string) => void;
  onHeight: (height: number) => void;
  onHidden: (hidden: boolean) => void;
  onLocked: (locked: boolean) => void;
}) {
  return (
    <div className="site-building-properties">
      <label>Name<input value={module.display_name ?? ''} onChange={(event) => onRename(event.target.value)} /></label>
      <label>Roof height (mm)<input type="number" value={module.roof_height_above_ground?.millimetres ?? 0}
        onChange={(event) => onHeight(Number(event.target.value) || 0)} /></label>
      <label className="checkbox-field"><input type="checkbox" checked={hidden} onChange={(event) => onHidden(event.target.checked)} />Hide in Site Layout</label>
      <label className="checkbox-field"><input type="checkbox" checked={locked} onChange={(event) => onLocked(event.target.checked)} />Lock placement</label>
    </div>
  );
}

function ModulePolygon({ module, selected, onSelect, onOpen }: {
  module: RoofModule;
  selected: boolean;
  onSelect: () => void;
  onOpen: () => void;
}) {
  const points = module.build_up?.plan?.outline?.points ?? [];
  if (points.length < 3) return null;
  const polygon = points.map((point) => `${point.x ?? 0},${point.y ?? 0}`).join(' ');
  const centre = polygonCentre(points);
  return (
    <g
      className={selected ? 'site-module is-selected' : 'site-module'}
      onClick={onSelect}
      onDoubleClick={onOpen}
      tabIndex={0}
      role="button"
      aria-label={`${module.display_name || module.id || 'Roof'} in Site Layout. Double-click to open.`}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect();
        }
      }}
    >
      <polygon points={polygon} />
      <text className="site-module-label" transform={`translate(${centre.x} ${centre.y}) scale(1 -1)`}>
        {module.display_name || module.id || 'Roof'} · h {formatMetres(module.roof_height_above_ground?.millimetres ?? 0)}
      </text>
    </g>
  );
}

function SiteInteractionTable({ modules, summaries }: { modules: readonly RoofModule[]; summaries: ReturnType<typeof summarizeSitePairs> }) {
  if (summaries.length === 0) return null;
  const name = (id: string) => modules.find((module) => module.id === id)?.display_name || id;
  return (
    <section className="site-interactions">
      <h3>Roof interaction diagnostics</h3>
      <table className="engineering-table">
        <thead><tr><th>Pair</th><th>Geometry</th><th>Engineering state</th><th>Gap</th><th>Shared edge</th><th>Δh</th></tr></thead>
        <tbody>
          {summaries.map((summary) => (
            <tr key={`${summary.moduleAId}:${summary.moduleBId}`} title={summary.blockingReason}>
              <td>{name(summary.moduleAId)} / {name(summary.moduleBId)}</td>
              <td>{summary.geometricRelation}</td>
              <td>{summary.engineeringRelation}</td>
              <td>{formatMetres(summary.minimumSeparationMm)}</td>
              <td>{formatMetres(summary.sharedEdgeLengthMm)}</td>
              <td>{formatMetres(summary.deltaHeightMm)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="field-note">Geometric contact is diagnostic only. Same-level contact is not automatically treated as aerodynamic continuity, and different-height attachment remains source-gated.</p>
    </section>
  );
}

function transformModulePlan(module: RoofModule, transform: (point: Point2D) => Point2D): RoofModule {
  const plan = module.build_up?.plan;
  if (!plan?.outline) return module;
  const nextPlan: RoofPlanGeometry = {
    ...plan,
    revision: (plan.revision ?? 0) + 1,
    outline: transformPolygon(plan.outline, transform),
    openings: (plan.openings ?? []).map((opening) => ({
      ...opening,
      outline: opening.outline ? transformPolygon(opening.outline, transform) : opening.outline,
    })),
    gross_area: undefined,
    net_area: undefined,
    perimeter: undefined,
    dimensions: undefined,
    wind_zones: undefined,
    messages: undefined,
  };
  return {
    ...module,
    build_up: {
      ...module.build_up,
      plan: nextPlan,
    },
  };
}

function transformPolygon(polygon: Polygon2D, transform: (point: Point2D) => Point2D): Polygon2D {
  return {
    ...polygon,
    points: (polygon.points ?? []).map(transform),
  };
}

function rotateModule(module: RoofModule, degrees: number): RoofModule {
  const points = module.build_up?.plan?.outline?.points ?? [];
  const centre = polygonCentre(points);
  const radians = degrees * Math.PI / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return transformModulePlan(module, (point) => {
    const dx = (point.x ?? 0) - centre.x;
    const dy = (point.y ?? 0) - centre.y;
    return {
      ...point,
      x: centre.x + dx * cos - dy * sin,
      y: centre.y + dx * sin + dy * cos,
    };
  });
}

function duplicateModule(source: RoofModule, ordinal: number): RoofModule {
  const clone = deepClone(source);
  clone.id = uniqueId('roof-module');
  clone.display_name = `Roof ${ordinal}`;

  const pointIdMap = new Map<string, string>();
  const reidentifyAndOffset = (point: Point2D): Point2D => {
    const oldId = point.id ?? uniqueId('source-point');
    const nextId = uniqueId('point');
    pointIdMap.set(oldId, nextId);
    return { ...point, id: nextId, x: (point.x ?? 0) + DEFAULT_OFFSET_MM, y: (point.y ?? 0) + DEFAULT_OFFSET_MM };
  };
  const plan = clone.build_up?.plan;
  if (!plan?.outline) return clone;
  const outlinePoints = (plan.outline.points ?? []).map(reidentifyAndOffset);
  const nextPlan = withPlanPoints(plan, outlinePoints);
  nextPlan.edge_properties = (plan.edge_properties ?? []).map((edge) => {
    const oldPointId = edge.segment_id?.startsWith('segment-') ? edge.segment_id.slice('segment-'.length) : undefined;
    const newPointId = oldPointId ? pointIdMap.get(oldPointId) : undefined;
    return newPointId ? { ...edge, segment_id: `segment-${newPointId}` } : { ...edge };
  });
  nextPlan.openings = (plan.openings ?? []).map((opening) => {
    if (!opening.outline) return { ...opening, id: uniqueId('opening') };
    const points = (opening.outline.points ?? []).map((point) => ({
      ...point,
      id: uniqueId('point'),
      x: (point.x ?? 0) + DEFAULT_OFFSET_MM,
      y: (point.y ?? 0) + DEFAULT_OFFSET_MM,
    }));
    return {
      ...opening,
      id: uniqueId('opening'),
      outline: {
        ...opening.outline,
        points,
        segments: points.map((point, index) => ({
          id: `segment-${point.id}`,
          start_point_id: point.id,
          end_point_id: points[(index + 1) % points.length]?.id ?? point.id,
        })),
      },
    };
  });
  nextPlan.gross_area = undefined;
  nextPlan.net_area = undefined;
  nextPlan.perimeter = undefined;
  nextPlan.dimensions = undefined;
  nextPlan.wind_zones = undefined;
  nextPlan.messages = undefined;
  clone.build_up = { ...clone.build_up, plan: nextPlan };
  return clone;
}

function withPlanPoints(plan: RoofPlanGeometry, points: Point2D[]): RoofPlanGeometry {
  return {
    ...plan,
    revision: (plan.revision ?? 0) + 1,
    outline: {
      ...plan.outline,
      points,
      segments: points.map((point, index) => ({
        id: `segment-${point.id}`,
        start_point_id: point.id,
        end_point_id: points[(index + 1) % points.length]?.id ?? point.id,
      })),
    },
  };
}

function emptyModule(ordinal: number): RoofModule {
  const id = uniqueId('roof-module');
  const points: Point2D[] = [
    { id: uniqueId('point'), x: 0, y: 0 },
    { id: uniqueId('point'), x: 10000, y: 0 },
    { id: uniqueId('point'), x: 10000, y: 10000 },
    { id: uniqueId('point'), x: 0, y: 10000 },
  ];
  return {
    id,
    display_name: `Roof ${ordinal}`,
    quantity_multiplier: 1,
    roof_height_above_ground: { millimetres: 10000, origin: 'VALUE_ORIGIN_INPUT' },
    roof_slope: { degrees: 0, origin: 'VALUE_ORIGIN_INPUT' },
    build_up: { plan: withPlanPoints({}, points) },
  };
}

function polygonCentre(points: readonly Point2D[]): { x: number; y: number } {
  if (points.length === 0) return { x: 0, y: 0 };
  const xs = points.map((point) => point.x ?? 0);
  const ys = points.map((point) => point.y ?? 0);
  return {
    x: (Math.min(...xs) + Math.max(...xs)) / 2,
    y: (Math.min(...ys) + Math.max(...ys)) / 2,
  };
}

function moduleBounds(module: RoofModule): { minX: number; minY: number; maxX: number; maxY: number } {
  const points = module.build_up?.plan?.outline?.points ?? [];
  if (points.length === 0) return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  const xs = points.map((point) => point.x ?? 0);
  const ys = points.map((point) => point.y ?? 0);
  return {
    minX: Math.min(...xs),
    minY: Math.min(...ys),
    maxX: Math.max(...xs),
    maxY: Math.max(...ys),
  };
}

function siteViewBox(modules: readonly RoofModule[]): ViewBox {
  const points = modules.flatMap((module) => module.build_up?.plan?.outline?.points ?? []);
  if (points.length === 0) return { minX: -1000, minY: -1000, width: 12000, height: 12000 };
  const xs = points.map((point) => point.x ?? 0);
  const ys = points.map((point) => point.y ?? 0);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const margin = Math.max(2000, Math.max(maxX - minX, maxY - minY) * 0.12);
  return {
    minX: minX - margin,
    minY: minY - margin,
    width: Math.max(1, maxX - minX + 2 * margin),
    height: Math.max(1, maxY - minY + 2 * margin),
  };
}

function toggleSet(current: Set<string>, id: string, enabled: boolean): Set<string> {
  const next = new Set(current);
  if (enabled) next.add(id);
  else next.delete(id);
  return next;
}

function uniqueId(prefix: string): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return `${prefix}-${crypto.randomUUID()}`;
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function deepClone<T>(value: T): T {
  if (typeof structuredClone === 'function') return structuredClone(value);
  return JSON.parse(JSON.stringify(value)) as T;
}

function formatMetres(valueMm: number): string {
  if (!Number.isFinite(valueMm)) return '-';
  return `${(valueMm / 1000).toFixed(2)} m`;
}
